// Outline import for the custom shape editor: closed outlines from SVG
// (polygon, polyline, rect, and paths with straight segments) and DXF
// (LWPOLYLINE, POLYLINE, CIRCLE). Curved SVG commands are rejected with a
// clear message rather than silently mangled; flatten to lines first.

import type { Vec2 } from '../core/types'

export interface ImportedOutline {
  nodes: Vec2[]
  source: 'svg' | 'dxf'
}

export function parseOutlineFile(name: string, text: string): ImportedOutline | { error: string } {
  const lower = name.toLowerCase()
  if (lower.endsWith('.svg') || text.trimStart().startsWith('<')) {
    return parseSvgOutline(text)
  }
  if (lower.endsWith('.dxf') || /(^|\r?\n)\s*0\r?\nSECTION/.test(text)) {
    return parseDxfOutline(text)
  }
  return { error: 'Unrecognized file: use an SVG or DXF outline' }
}

function ringArea(nodes: Vec2[]): number {
  let s = 0
  for (let i = 0; i < nodes.length; i++) {
    const a = nodes[i]
    const b = nodes[(i + 1) % nodes.length]
    s += a.x * b.y - b.x * a.y
  }
  return Math.abs(s / 2)
}

function best(cands: Vec2[][], source: 'svg' | 'dxf'): ImportedOutline | { error: string } {
  const usable = cands.filter((c) => c.length >= 3 && ringArea(c) > 1e-9)
  if (usable.length === 0) return { error: 'No closed outline found in that file' }
  usable.sort((a, b) => ringArea(b) - ringArea(a))
  return { nodes: usable[0], source }
}

// Regex-scoped SVG reading (no DOMParser): keeps the parser environment-free
// so it runs in tests and workers alike. Only the supported subset is read.
function attr(tag: string, name: string): string | null {
  const m = tag.match(new RegExp(`${name}\\s*=\\s*"([^"]*)"`)) ?? tag.match(new RegExp(`${name}\\s*=\\s*'([^']*)'`))
  return m ? m[1] : null
}

function parseSvgOutline(text: string): ImportedOutline | { error: string } {
  const cands: Vec2[][] = []
  const tags = text.match(/<(polygon|polyline|rect|path)\b[^>]*>/g) ?? []

  for (const tag of tags.filter((t) => t.startsWith('<polygon') || t.startsWith('<polyline'))) {
    const pts = (attr(tag, 'points') ?? '')
      .trim()
      .split(/[\s,]+/)
      .map(Number)
    const nodes: Vec2[] = []
    for (let i = 0; i + 1 < pts.length; i += 2) {
      if (Number.isFinite(pts[i]) && Number.isFinite(pts[i + 1])) nodes.push({ x: pts[i], y: pts[i + 1] })
    }
    cands.push(nodes)
  }

  for (const tag of tags.filter((t) => t.startsWith('<rect'))) {
    const x = parseFloat(attr(tag, 'x') ?? '0')
    const y = parseFloat(attr(tag, 'y') ?? '0')
    const w = parseFloat(attr(tag, 'width') ?? '0')
    const h = parseFloat(attr(tag, 'height') ?? '0')
    if (w > 0 && h > 0) {
      cands.push([
        { x, y },
        { x: x + w, y },
        { x: x + w, y: y + h },
        { x, y: y + h },
      ])
    }
  }

  let sawCurves = false
  for (const tag of tags.filter((t) => t.startsWith('<path'))) {
    const d = attr(tag, 'd') ?? ''
    if (/[csqtaCSQTA]/.test(d)) {
      sawCurves = true
      continue
    }
    const nodes: Vec2[] = []
    let cx = 0
    let cy = 0
    const tokens = d.match(/[mlhvzMLHVZ]|-?\d*\.?\d+(?:e-?\d+)?/g) ?? []
    let i = 0
    let cmd = ''
    const numAt = (k: number) => parseFloat(tokens[k])
    while (i < tokens.length) {
      const t = tokens[i]
      if (/[a-zA-Z]/.test(t)) {
        cmd = t
        i++
        if (cmd === 'z' || cmd === 'Z') continue
        continue
      }
      switch (cmd) {
        case 'M':
        case 'L':
          cx = numAt(i)
          cy = numAt(i + 1)
          i += 2
          break
        case 'm':
        case 'l':
          cx += numAt(i)
          cy += numAt(i + 1)
          i += 2
          break
        case 'H':
          cx = numAt(i)
          i += 1
          break
        case 'h':
          cx += numAt(i)
          i += 1
          break
        case 'V':
          cy = numAt(i)
          i += 1
          break
        case 'v':
          cy += numAt(i)
          i += 1
          break
        default:
          i += 1
          continue
      }
      nodes.push({ x: cx, y: cy })
      // Successive coordinate pairs after M behave as L.
      if (cmd === 'M') cmd = 'L'
      if (cmd === 'm') cmd = 'l'
    }
    cands.push(nodes)
  }

  const res = best(cands, 'svg')
  if ('error' in res && sawCurves) {
    return { error: 'That SVG uses curves; flatten it to line segments first' }
  }
  return res
}

function parseDxfOutline(text: string): ImportedOutline | { error: string } {
  const lines = text.split(/\r?\n/)
  const cands: Vec2[][] = []
  let i = 0
  const read = () => ({ code: lines[i]?.trim(), value: lines[i + 1]?.trim() })

  while (i + 1 < lines.length) {
    const { code, value } = read()
    if (code === '0' && (value === 'LWPOLYLINE' || value === 'POLYLINE')) {
      const nodes: Vec2[] = []
      const isLw = value === 'LWPOLYLINE'
      i += 2
      let px: number | null = null
      while (i + 1 < lines.length) {
        const t = read()
        if (t.code === '0' && isLw) break
        if (t.code === '0' && !isLw && t.value === 'SEQEND') {
          i += 2
          break
        }
        if (t.code === '10') px = parseFloat(t.value ?? '')
        if (t.code === '20' && px !== null) {
          const y = parseFloat(t.value ?? '')
          if (Number.isFinite(px) && Number.isFinite(y)) nodes.push({ x: px, y })
          px = null
        }
        i += 2
      }
      cands.push(nodes)
      continue
    }
    if (code === '0' && value === 'CIRCLE') {
      i += 2
      let cx = 0
      let cy = 0
      let r = 0
      while (i + 1 < lines.length) {
        const t = read()
        if (t.code === '0') break
        if (t.code === '10') cx = parseFloat(t.value ?? '0')
        if (t.code === '20') cy = parseFloat(t.value ?? '0')
        if (t.code === '40') r = parseFloat(t.value ?? '0')
        i += 2
      }
      if (r > 0) {
        const nodes: Vec2[] = []
        for (let k = 0; k < 48; k++) {
          const a = (k / 48) * Math.PI * 2
          nodes.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) })
        }
        cands.push(nodes)
      }
      continue
    }
    i += 2
  }
  return best(cands, 'dxf')
}

export function pickOutlineFile(
  onLoaded: (outline: ImportedOutline) => void,
  onFail: (message: string) => void,
) {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.svg,.dxf,image/svg+xml'
  input.onchange = async () => {
    const file = input.files?.[0]
    if (!file) return
    const res = parseOutlineFile(file.name, await file.text())
    if ('error' in res) onFail(res.error)
    else onLoaded(res)
  }
  input.click()
}
